import {
  QuestionnaireInterface,
  QuestionnaireResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import { showToast } from '@shared/atoms';
import { SingleQuestionnaire } from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { setChosenQuestionnaire } from 'store/Slices/QuestionnaireSliceV2';

export const OnboardingAssessmentPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isDesktop } = useWindowDimensions();
  const company = localStorage.getItem('company');
  const parsedCompany = JSON.parse(company || '{}');
  const { chosenQuestionnaire } = useAppSelector((state) => state.questionnaireV2 || {});
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchOnboardingQuestionnaires = async (): Promise<ApiResponse<QuestionnaireResponse[]>> =>
    assessmentQuestionnaireApiHandler.getAllCompanyOnboardingQuestionnaires();

  const fetchQuestionsByQuestionnaire = async (): Promise<
    ApiResponse<QuestionnaireInterface> | undefined
  > => {
    if (!chosenQuestionnaire || !chosenQuestionnaire.questionnaireId) {
      showToast('Please select a questionnaire from the list', undefined, 'error');

      return undefined;
    }

    return assessmentQuestionnaireApiHandler.getQuestionsByQuestionnaire(
      chosenQuestionnaire.questionnaireId,
    );
  };

  const {
    data: questionnairesData,
    isLoading: questionnairesLoading,
    error: questionnairesError,
  } = useQuery({
    queryKey: ['getAllCompanyOnboardingQuestionnaires', parsedCompany.id],
    queryFn: fetchOnboardingQuestionnaires,
    enabled: !!parsedCompany?.id,
  });

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
    isPending,
  } = useQuery({
    queryKey: ['getOnboardingQuestionsByQuestionnaire', chosenQuestionnaire?.questionnaireId],
    queryFn: fetchQuestionsByQuestionnaire,
    enabled: !!(parsedCompany?.id && chosenQuestionnaire?.questionnaireId),
  });

  const questionnaires = useMemo(
    () => (questionnairesData?.success ? questionnairesData.responseObject : []),
    [questionnairesData],
  );

  useEffect(() => {
    if (questionnaires.length > 0) {
      dispatch(setChosenQuestionnaire(questionnaires[currentIndex]));
    }
  }, [questionnaires, currentIndex, dispatch]);

  const totalQuestionnaires = questionnaires?.length;
  const isLastQuestionnaireInBoarding = currentIndex === totalQuestionnaires - 1;

  const handleNextQuestionnaire = () => {
    const today = new Date().toISOString().split('T')[0];
    setCurrentIndex((prevIndex) => (prevIndex + 1) % questionnaires.length);

    if (chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.CHECK_IN) {
      let localStorageData: LocalStorageData = {};
      const storedData = localStorage.getItem('shortStarCacheHistory');

      if (!storedData) {
        localStorageData = {
          lastPopupDate: today,
          popupClosedToday: true,
        };
        localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
      }
    }

    // if (isLastQuestionnaireInBoarding) {
    //   showToast(
    //     ToastMessages.onboarding.success.ONBOARDING_COMPLETED,
    //     {
    //       autoClose: 100,
    //       onClose: async () => {
    //         await dispatch(updateUserStatusData(statusData));
    //         window.location.href = '/';
    //       },
    //     },
    //     'success',
    //   );
    // }
  };

  return (
    <CoreLayout title="Onboarding Assessment Page" navigationType="default">
      <SingleQuestionnaire
        companyQuestionnaires={questionnaires}
        questionsData={questionsData}
        questionnairesLoading={questionnairesLoading}
        questionnairesError={questionnairesError}
        questionsLoading={questionsLoading || isPending}
        questionsError={questionsError}
        isOnboarding
        onComplete={handleNextQuestionnaire}
        isLastQuestionnaireInBoarding={isLastQuestionnaireInBoarding}
      />
    </CoreLayout>
  );
};

interface LocalStorageData {
  lastPopupDate?: string;
  popupClosedToday?: boolean;
}
